import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, DialogTitle,
    Button, Typography, Box, CircularProgress, Chip,
    Table, TableHead, TableBody, TableRow, TableCell, Switch, Tooltip
} from '@material-ui/core';

import {
    getInsiderProductAccessService,
    setInsiderOverridesService,
    removeInsiderOverrideService,
    resetInsiderOverridesService,
    listProductDefinitionsService
} from "../../../../services/pac.service";
import { toastSuccess, toastWarning } from "../../../../utils/utils";
import LoaderWithBackDrop from "../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";

const FULL = { view: true, create: true, link: true };

const InsiderProductAccessDialog = ({ open, onClose, onClosed, dq_insider_id }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // rows: [{ product_key, label, description, enabled, permissions, source, hasOverride, locked }]
    const [rows, setRows] = useState([]);
    // product_keys changed by the admin this session
    const [changed, setChanged] = useState(new Set());

    const handleClose = () => {
        onClose();
        if (typeof onClosed === 'function') onClosed();
    };

    const loadData = async () => {
        if (!dq_insider_id) return;
        setLoading(true);
        try {
            const [accessRes, defsRes] = await Promise.all([
                getInsiderProductAccessService(dq_insider_id),
                listProductDefinitionsService()
            ]);

            const overridesData = (accessRes.data && accessRes.data.overrides) || {};
            const resolvedData = (accessRes.data && accessRes.data.resolved) || {};
            const products = (defsRes.data && defsRes.data.products) || [];

            const mapped = products.map(p => {
                const key = p.product_key;
                const ovr = overridesData[key];
                const res = resolvedData[key] || {};
                const enabled = ovr ? !!ovr.enabled : !!res.enabled;
                const permissions = {
                    ...FULL,
                    ...(p.default_permissions || {}),
                    ...((ovr && ovr.permissions) || res.permissions || {}),
                };
                return {
                    product_key: key,
                    label: p.label,
                    description: p.description,
                    enabled,
                    permissions: { view: permissions.view !== false, create: permissions.create !== false, link: permissions.link !== false },
                    source: res.source || (ovr ? 'insider_override' : 'default_disabled'),
                    hasOverride: !!ovr,
                    locked: res.source === 'global_disabled',
                };
            });

            setRows(mapped);
            setChanged(new Set());
        } catch (e) {
            toastWarning("Failed to load Product Access.");
        }
        setLoading(false);
    };

    useEffect(() => {
        if (open) {
            loadData();
        } else {
            setRows([]);
            setChanged(new Set());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dq_insider_id]);

    const markChanged = (product_key) => {
        setChanged(prev => {
            const next = new Set(prev);
            next.add(product_key);
            return next;
        });
    };

    const toggleEnabled = (product_key) => {
        setRows(prev => prev.map(r => r.product_key === product_key ? { ...r, enabled: !r.enabled } : r));
        markChanged(product_key);
    };

    const togglePermission = (product_key, dim) => {
        setRows(prev => prev.map(r =>
            r.product_key === product_key
                ? { ...r, permissions: { ...r.permissions, [dim]: !r.permissions[dim] } }
                : r
        ));
        markChanged(product_key);
    };

    const handleSave = async () => {
        setSubmitting(true);
        try {
            // Send overrides for products that have an existing override OR were changed.
            const toSend = {};
            rows.forEach(r => {
                if (r.locked) return;
                if (r.hasOverride || changed.has(r.product_key)) {
                    toSend[r.product_key] = { enabled: r.enabled, permissions: r.permissions };
                }
            });
            if (Object.keys(toSend).length === 0) {
                toastWarning("No changes to save.");
                setSubmitting(false);
                return;
            }
            await setInsiderOverridesService(dq_insider_id, toSend);
            toastSuccess("Product access overrides saved.");
            handleClose();
        } catch (e) {
            toastWarning("Failed to save overrides.");
        }
        setSubmitting(false);
    };

    const handleClearOverride = async (product_key) => {
        setSubmitting(true);
        try {
            await removeInsiderOverrideService(dq_insider_id, product_key);
            toastSuccess(`Override cleared for ${product_key}.`);
            loadData();
        } catch (e) {
            toastWarning("Failed to clear override.");
        }
        setSubmitting(false);
    };

    const handleReset = async () => {
        if (!window.confirm("Reset all overrides to defaults?")) return;
        setSubmitting(true);
        try {
            await resetInsiderOverridesService(dq_insider_id);
            toastSuccess("Overrides reset.");
            loadData();
        } catch (e) {
            toastWarning("Failed to reset overrides.");
        }
        setSubmitting(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Manage Insider Product Access</DialogTitle>
            <DialogContent dividers style={{ minHeight: 300 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Set product access and granular permissions (view / create / link) for this DQ Insider.
                            Admin overrides replace plan and add-on access for that product. Clear an override to fall
                            back to plan/add-on resolution.
                        </Typography>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Product</TableCell>
                                    <TableCell align="center">Enabled</TableCell>
                                    <TableCell align="center">View</TableCell>
                                    <TableCell align="center">Create</TableCell>
                                    <TableCell align="center">Link</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">Override</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(r => (
                                    <TableRow key={r.product_key}>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gridGap={8}>
                                                <span><strong>{r.label}</strong><br /><small>{r.product_key}</small></span>
                                                {(r.hasOverride || changed.has(r.product_key)) && (
                                                    <Chip size="small" label="Override" color="primary" style={{ height: 16, fontSize: '0.6rem' }} />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Switch size="small" color="primary"
                                                    checked={r.enabled}
                                                    disabled={r.locked}
                                                    onChange={() => toggleEnabled(r.product_key)} />
                                        </TableCell>
                                        {['view', 'create', 'link'].map(dim => (
                                            <TableCell align="center" key={dim}>
                                                <Switch size="small" color="primary"
                                                        checked={r.enabled && r.permissions[dim]}
                                                        disabled={r.locked || !r.enabled}
                                                        onChange={() => togglePermission(r.product_key, dim)} />
                                            </TableCell>
                                        ))}
                                        <TableCell><small>{r.locked ? 'global_disabled' : r.source}</small></TableCell>
                                        <TableCell align="right">
                                            {r.hasOverride ? (
                                                <Tooltip title="Clear override (fall back to plan/add-on)">
                                                    <Button size="small" onClick={() => handleClearOverride(r.product_key)}>Clear</Button>
                                                </Tooltip>
                                            ) : (
                                                <span style={{ color: '#bbb' }}>—</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {rows.length === 0 && !loading && (
                            <Typography color="textSecondary">No products found.</Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions style={{ justifyContent: 'space-between', padding: '16px' }}>
                <Button onClick={handleReset} color="secondary" variant="outlined" disabled={submitting || loading}>
                    Reset to Defaults
                </Button>
                <Box>
                    <Button onClick={handleClose} color="default" style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} color="primary" variant="contained" disabled={submitting || loading}>
                        {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save Overrides'}
                    </Button>
                </Box>
            </DialogActions>

            <LoaderWithBackDrop loading={submitting} />
        </Dialog>
    );
};

export default InsiderProductAccessDialog;
